import { cn } from 'noddi-ui-common';
import { ReactNode, useId } from 'react';
import type { MultiSelectOption } from '../../../base/multi-select-utils';
import { NoddiTooltip } from '../../Overlay/NoddiTooltip';
import { MultiSelect } from './MultiSelect';

type Value = string | number;

export interface NoddiMultiSelectProps {
  options: MultiSelectOption[];
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onChange: (values: Value[]) => void;
  defaultValue?: Value[] | undefined;
  placeholder?: string;
  className?: string;
  label?: ReactNode;
  /** `sr-only` keeps the label for screen readers where the placeholder already says what the control is. */
  labelClassName?: string;
  tooltip?: string;
  maxCount?: number;
  hideSelectAll?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  renderOption?: (option: MultiSelectOption) => ReactNode;
}

export const NoddiMultiSelect = ({
  options,
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  defaultValue,
  onChange,
  placeholder,
  className,
  label,
  labelClassName,
  tooltip,
  maxCount = 3,
  hideSelectAll,
  searchable,
  disabled,
  renderOption
}: NoddiMultiSelectProps) => {
  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;

  return (
    <div className='flex flex-col gap-0.5'>
      {label && (
        <div className={cn('flex items-center gap-2', labelClassName)}>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && (
            <NoddiTooltip
              content={tooltip}
              label={typeof label === 'string' ? `More information about ${label}` : 'More information'}
            />
          )}
        </div>
      )}
      <MultiSelect
        searchable={searchable}
        hideSelectAll={hideSelectAll}
        options={options}
        id={controlId}
        name={name}
        aria-labelledby={label ? labelIdentifier : undefined}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        defaultValue={defaultValue}
        onValueChange={onChange}
        placeholder={placeholder}
        className={className}
        maxCount={maxCount}
        disabled={disabled}
        renderOption={renderOption}
      />
    </div>
  );
};
